import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ReceiptX, CheckCircle, Files, Sparkle, Package, Clock, Users, MicrophoneStage } from "@phosphor-icons/react";

export default function QuoteAndTemplateSection({ chantier, onChanged }) {
  const [flagOpen, setFlagOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [note, setNote] = useState(chantier.quote_note || "");
  const [saving, setSaving] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [ctxLoading, setCtxLoading] = useState(false);

  useEffect(() => {
    setNote(chantier.quote_note || "");
  }, [chantier.id, chantier.quote_note]);

  // Fetch aggregated quote context (materials + hours + team + debriefs) when opening
  useEffect(() => {
    if (!flagOpen) return;
    setCtxLoading(true);
    api.get(`/chantiers/${chantier.id}/quote-context`)
      .then((r) => {
        setCtx(r.data);
        if (!chantier.quote_note && r.data.suggested_note) setNote(r.data.suggested_note);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setCtxLoading(false));
  }, [flagOpen, chantier.id, chantier.quote_note]);

  const applySuggestions = () => {
    if (!ctx) return;
    setNote(ctx.suggested_note || "");
    toast.success("Contexte auto-appliqué");
  };

  const flag = async (to_quote) => {
    setSaving(true);
    try {
      await api.patch(`/chantiers/${chantier.id}/quote-flag`, {
        to_quote,
        quote_note: to_quote ? note : null,
      });
      toast.success(to_quote ? "Signalé au gérant pour devis" : "Signalement retiré");
      setFlagOpen(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const loadTemplates = async () => {
    const r = await api.get("/templates", { params: { category: "chantier" } });
    setTemplates(r.data);
  };

  const apply = async (tid) => {
    try {
      const r = await api.post(`/chantiers/${chantier.id}/apply-template/${tid}`);
      toast.success(`${r.data.tasks_created} tâches ajoutées depuis "${r.data.template_name}"`);
      setTplOpen(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isFlagged = !!chantier.to_quote;
  const isSent = !!chantier.quote_sent;

  return (
    <div className="grid grid-cols-2 gap-2">
      <button
        onClick={() => setFlagOpen(true)}
        className={`rounded-2xl border p-3 flex flex-col items-start gap-2 active:scale-[0.98] transition-transform ${
          isSent
            ? "bg-emerald-500/10 border-emerald-500/30"
            : isFlagged
              ? "bg-amber-400/10 border-amber-400/40"
              : "bg-white/[0.03] border-white/10 hover:border-amber-400/40"
        }`}
        data-testid="quote-flag-btn"
      >
        <div className="flex items-center gap-2">
          {isSent ? (
            <CheckCircle size={18} className="text-emerald-400" weight="duotone" />
          ) : (
            <ReceiptX size={18} className={isFlagged ? "text-amber-400" : "text-white/50"} weight="duotone" />
          )}
          <span className="font-medium text-sm">
            {isSent ? "Devis envoyé" : isFlagged ? "En attente devis" : "Passer en devis"}
          </span>
        </div>
        <div className="text-[10px] text-white/50 text-left">
          {isSent
            ? `Ref: ${chantier.axonaut_ref || "—"}`
            : isFlagged
              ? "Signalé au gérant"
              : "Signaler au gérant"}
        </div>
      </button>

      <button
        onClick={() => { loadTemplates(); setTplOpen(true); }}
        className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#005CFF]/40 p-3 flex flex-col items-start gap-2 active:scale-[0.98] transition-transform"
        data-testid="apply-template-btn"
      >
        <div className="flex items-center gap-2">
          <Files size={18} className="text-[#005CFF]" weight="duotone" />
          <span className="font-medium text-sm">Fiche type</span>
        </div>
        <div className="text-[10px] text-white/50 text-left">Charger une checklist</div>
      </button>

      <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
        <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="quote-flag-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ReceiptX size={20} className="text-amber-400" weight="duotone" /> Passer en devis
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-white/60">
              Cette action envoie ce chantier dans l&apos;inbox « À passer en devis » de la console gérant, pour préparation dans Axonaut.
            </div>

            {/* Auto-aggregated context — materials + hours + team + debriefs (no money) */}
            {ctxLoading ? (
              <div className="h-24 rounded-xl bg-white/[0.03] border border-white/10 animate-pulse" />
            ) : ctx && (ctx.materials.length > 0 || ctx.labor_minutes > 0 || ctx.team.length > 0 || ctx.debriefs.length > 0 || ctx.ai_summary) ? (
              <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30 p-3 space-y-3" data-testid="quote-context-preview">
                <div className="flex items-center gap-2">
                  <Sparkle size={14} weight="fill" className="text-emerald-400" />
                  <div className="text-xs font-semibold">Récapitulatif chantier</div>
                  <button
                    type="button"
                    onClick={applySuggestions}
                    className="ml-auto text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300"
                    data-testid="quote-context-apply"
                  >
                    Réappliquer
                  </button>
                </div>

                {/* Compact stats row: hours + counts */}
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div className="rounded-lg bg-black/40 p-2 text-center">
                    <div className="flex items-center justify-center gap-1 text-white/50 text-[9px] uppercase tracking-widest">
                      <Clock size={9} /> Temps
                    </div>
                    <div className="font-display font-bold text-[#7CB2FF]">{ctx.labor_hours_label}</div>
                  </div>
                  <div className="rounded-lg bg-black/40 p-2 text-center">
                    <div className="flex items-center justify-center gap-1 text-white/50 text-[9px] uppercase tracking-widest">
                      <Package size={9} /> Matériel
                    </div>
                    <div className="font-display font-bold text-emerald-400">{ctx.materials.length}</div>
                    <div className="text-[9px] text-white/40">références</div>
                  </div>
                  <div className="rounded-lg bg-black/40 p-2 text-center">
                    <div className="flex items-center justify-center gap-1 text-white/50 text-[9px] uppercase tracking-widest">
                      <Users size={9} /> Équipe
                    </div>
                    <div className="font-display font-bold text-white">{ctx.team.length}</div>
                  </div>
                </div>

                {/* Team list with per-tech hours */}
                {ctx.team.length > 0 && (
                  <div className="rounded-lg bg-black/40 p-2 space-y-1">
                    <div className="text-[9px] uppercase tracking-widest text-white/50 mb-1 flex items-center gap-1">
                      <Users size={9} /> Équipiers affectés
                    </div>
                    {ctx.team.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 text-[11px]">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: t.color || "#7CB2FF" }}
                        />
                        <span className="flex-1 truncate">{t.name}</span>
                        {t.minutes > 0 && (
                          <span className="text-[#7CB2FF] font-mono tabular-nums text-[10px]">
                            {t.hours_label}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Materials list — labels + qty only */}
                {ctx.materials.length > 0 && (
                  <div className="rounded-lg bg-black/40 p-2 space-y-0.5 max-h-32 overflow-y-auto">
                    <div className="text-[9px] uppercase tracking-widest text-white/50 mb-1 flex items-center gap-1">
                      <Package size={9} /> Liste matériel
                    </div>
                    {ctx.materials.map((m) => (
                      <div key={m.label} className="flex justify-between gap-2 text-[11px]">
                        <span className="truncate flex-1">{m.label}</span>
                        <span className="text-emerald-400 font-mono tabular-nums shrink-0">×{m.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* AI summary + individual debriefs */}
                {(ctx.ai_summary || ctx.debriefs.length > 0) && (
                  <div className="rounded-lg bg-black/40 p-2 space-y-2 max-h-40 overflow-y-auto">
                    <div className="text-[9px] uppercase tracking-widest text-white/50 flex items-center gap-1">
                      <MicrophoneStage size={9} /> Débriefs IA ({ctx.debriefs.length})
                    </div>
                    {ctx.ai_summary && (
                      <div className="rounded bg-[#005CFF]/10 border border-[#005CFF]/20 p-1.5 text-[10.5px] text-white/85 leading-relaxed">
                        <span className="text-[#7CB2FF] font-semibold text-[9px] uppercase tracking-widest">Synthèse IA · </span>
                        {ctx.ai_summary}
                      </div>
                    )}
                    {ctx.debriefs.map((d) => (
                      <div key={d.id} className="text-[10.5px] leading-relaxed">
                        <span className="text-white/50 text-[9px] uppercase tracking-widest">
                          {d.author_name || "Tech"} ·
                        </span>{" "}
                        <span className="text-white/80">{d.transcription}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <div>
              <Label className="text-white/60 text-[11px] uppercase tracking-widest">Note pour le gérant</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ex : rajouter câblage prise dédiée, remplacement disjoncteur, etc."
                rows={6}
                className="bg-white/5 border-white/10 mt-1 font-mono text-[11px]"
                data-testid="quote-note-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              {isFlagged && (
                <Button variant="ghost" onClick={() => flag(false)} disabled={saving} className="flex-1 border border-white/10 text-red-400 hover:bg-red-500/10">
                  Retirer
                </Button>
              )}
              <Button onClick={() => flag(true)} disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="quote-flag-save">
                {saving ? "…" : isFlagged ? "Mettre à jour" : "Signaler"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="template-picker">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Files size={20} className="text-[#005CFF]" weight="duotone" /> Choisir une fiche type
            </DialogTitle>
          </DialogHeader>
          {templates.length === 0 ? (
            <div className="text-sm text-white/60 py-6 text-center">
              Aucune fiche type. Le gérant peut en créer depuis la console gérant.
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => apply(t.id)}
                  className="w-full text-left rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#005CFF]/40 p-3 flex items-center gap-3 active:scale-[0.98] transition-transform"
                  data-testid={`tpl-pick-${t.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-[#005CFF]/20 flex items-center justify-center">
                    <Files size={16} weight="duotone" className="text-[#005CFF]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-xs text-white/50">{t.items?.length || 0} tâches</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
